import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import AdminShell from "../../components/AdminShell";
import { Badge, Button, Card, StatusBadge } from "../../components/ui-kit";

export default function AdminDrivers() {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/drivers");
      setDrivers(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    try {
      await api.post(`/admin/drivers/${id}/approve`);
      toast.success("Chauffeur approuve");
      load();
    } catch (e) {
      toast.error(formatApiError(e), { duration: 9000 });
    }
  };

  const reject = async (id) => {
    try {
      await api.post(`/admin/drivers/${id}/reject`);
      toast.success("Chauffeur bloque");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <AdminShell title="Chauffeurs">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tighter sm:text-4xl">Chauffeurs</h1>
          <p className="text-sm text-muted-foreground">Details, vehicule, documents et validation.</p>
        </div>
        <Badge tone="primary">{drivers.length} comptes</Badge>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 xl:grid-cols-2">
        {loading && <div className="text-sm font-bold uppercase tracking-widest">Chargement...</div>}
        {drivers.map((d) => (
          <Card key={d.user.id} className="p-5" data-testid={`driver-card-${d.user.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="truncate font-heading text-xl font-black tracking-tight">{d.user.name}</div>
                <div className="truncate font-mono text-xs">{d.user.email}</div>
                <div className="font-mono text-xs">{d.user.phone}</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  <StatusBadge status={d.profile?.approval_status || "PENDING_VALIDATION"} />
                  <StatusBadge status={d.profile?.status || "offline"} />
                </div>
              </div>
              {d.vehicle && (
                <div className="shrink-0 text-right">
                  <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Vehicule</div>
                  <div className="font-heading text-lg font-black uppercase">{d.vehicle.vehicle_type}</div>
                  <div className="font-mono text-xs">{d.vehicle.plate} - {d.vehicle.model}</div>
                </div>
              )}
            </div>

            <div className="mt-4 grid grid-cols-1 gap-2 rounded-2xl bg-white/5 p-3 text-[11px] md:grid-cols-2">
              <div><span className="text-muted-foreground">Identite:</span> <span className="font-bold">{d.profile?.legal_full_name || "-"}</span></div>
              <div><span className="text-muted-foreground">Piece:</span> <span className="font-mono">{d.profile?.id_number || "-"}</span></div>
              <div><span className="text-muted-foreground">Adresse:</span> <span className="font-bold">{d.profile?.home_address || "-"}</span></div>
              <div><span className="text-muted-foreground">Zone:</span> <span className="font-bold">{d.profile?.operating_address || "-"}</span></div>
              <div><span className="text-muted-foreground">Urgence:</span> <span className="font-bold">{d.profile?.emergency_contact_name || "-"}</span></div>
              <div><span className="text-muted-foreground">Tel urgence:</span> <span className="font-mono">{d.profile?.emergency_contact_phone || "-"}</span></div>
            </div>

            <div className="mt-3 flex flex-wrap gap-2">
              <Badge tone={d.user.email_verified ? "success" : "warning"}>email {d.user.email_verified ? "verifie" : "non verifie"}</Badge>
              <Badge tone={d.user.phone_verified ? "success" : "warning"}>tel {d.user.phone_verified ? "verifie" : "non verifie"}</Badge>
              <Badge tone={d.profile?.details_status === "COMPLETED" ? "success" : "warning"}>details {d.profile?.details_status || "PENDING"}</Badge>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2">
              <Button
                variant="primary"
                onClick={() => approve(d.user.id)}
                data-testid={`approve-${d.user.id}`}
                disabled={d.profile?.approval_status === "ACTIVE"}
              >
                Approuver
              </Button>
              <Button variant="danger" onClick={() => reject(d.user.id)} data-testid={`reject-${d.user.id}`}>
                Bloquer
              </Button>
            </div>
            <Link
              to={`/admin/documents?driver=${d.user.id}`}
              className="mt-3 block rounded-full border border-white/10 bg-white/5 px-4 py-2 text-center text-[10px] font-bold uppercase tracking-widest text-primary hover:bg-white/10"
            >
              Ouvrir les documents
            </Link>
          </Card>
        ))}
        {!loading && drivers.length === 0 && (
          <Card className="p-8 text-center text-sm font-bold uppercase tracking-widest text-muted-foreground">Aucun chauffeur</Card>
        )}
      </div>
    </AdminShell>
  );
}
